import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Message } from '../Message';
import { AutoSizeInput } from '../../../../components/AutoSizeInput';
import { streamFetch } from '../../../../service/steamFetch';
import { useRequest } from 'ahooks';
import {
  getChatHistoryMessages,
  getChatLimits,
  getMoreList,
} from '../../service';
import { slice, throttle } from 'lodash-es';
import { msgBoxId } from '../../../../utils/const';
import { LikeStatus } from '../../type';
import { IMessageItem } from '../Message/MessageBox';
import foldSvg from './fold.svg';
import { PersonCenter } from '../PersonCenter';
import { useLanguageStore } from '../../../../hooks';
import useI18n from '../../../../hooks/useI18n';
import { message as AntdMessage } from 'antd';

let abortCtrl: AbortController | undefined;

export interface IChatBoxProps {
  selectChatId?: number;
  setSelectChatId: (p: number) => void;
  setRefreshHistory: React.Dispatch<React.SetStateAction<number>>;
  setFold: React.Dispatch<React.SetStateAction<boolean>>;
  isLogin: boolean;
  setIsLogin: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ChatBox: FC<IChatBoxProps> = (props) => {
  const {
    selectChatId,
    setSelectChatId,
    setRefreshHistory,
    setFold,
    isLogin,
    setIsLogin,
  } = props;
  const [value, setValue] = useState('');
  const [messages, setMessages] = useState<IMessageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [responding, setResponding] = useState(false);

  const [limitStatus, setLimitStatus] = useState(false);
  const [chatTotal, setChatTotal] = useState(10);

  const preQuestion = useRef<string>();
  const { data } = useRequest(
    () => getChatHistoryMessages(selectChatId?.toString()),
    {
      refreshDeps: [selectChatId],
      ready: !!selectChatId && isLogin,
    },
  );

  const canScroll = useRef(true);

  const { data: limitData } = useRequest(getChatLimits, {
    refreshDeps: [isLogin]
  });

  useEffect(() => {
    setLimitStatus(limitData?.data.data.limitStatus || false);
    setChatTotal(limitData?.data.data.limits || 10);
  }, [limitData]);

  useEffect(() => {
    if (!selectChatId) {
      setMessages([]);
      abortCtrl?.abort();
    }
  }, [selectChatId]);

  const { lang } = useLanguageStore();

  const { data: questionList } = useRequest(
    async () => await getMoreList({ lang }),
    {
      refreshDeps: [lang],
    },
  );

  const showWelcome = (!selectChatId && messages.length === 0) || !isLogin;

  const throttleScroll = useCallback(
    throttle(
      () => {
        const msgBox = document.getElementById(msgBoxId);
        if (msgBox && canScroll.current) {
          msgBox.scrollIntoView({
            behavior: 'smooth',
          });
        }
      },
      100,
      { trailing: false },
    ),
    [],
  );

  useEffect(() => {
    const historyMsg: IMessageItem[] = [];
    data?.data.data.list.forEach((item) => {
      historyMsg.push({
        type: 'User',
        content: item.userContent || '',
        likeStatus: item.likeStatus,
        id: `${item.id}User`,
      });
      historyMsg.push({
        type: 'Ai',
        content: item.aiContent || '',
        likeStatus: item.likeStatus,
        id: `${item.id}`,
      });
    });
    setMessages(historyMsg);
    setTimeout(() => {
      canScroll.current = true;
      throttleScroll();
    }, 300);
  }, [data?.data.data.list]);

  const sendMsg = async (v?: string) => {
    canScroll.current = true;
    const question = v || value;
    if (!question) return;
    if (loading) return;
    try {
      setMessages((pre) =>
        pre.concat([
          {
            type: 'User',
            content: question,
            likeStatus: LikeStatus.default,
            id: `${Date.now()}User`,
          },
          {
            type: 'Ai',
            content: '',
            likeStatus: LikeStatus.default,
            id: `${Date.now()}Answer`,
          },
        ]),
      );
      setTimeout(() => {
        throttleScroll();
      }, 100);
      setLoading(true);
      setResponding(true);
      preQuestion.current = question;
      setValue('');
      abortCtrl = new AbortController();
      await streamFetch({
        url: `${process.env.PUBLIC_API_URL}/chat/aiChattingStream`,
        data: {
          query: question,
          topicId: selectChatId,
        },
        onMessage: (e: {
          type: 'answer' | 'info' | 'remove';
          content: string;
        }) => {
          setLoading(false);
          throttleScroll();
          if (e.type === 'answer') {
            setMessages((pre) => {
              const last = pre[pre.length - 1];
              return [
                ...pre.slice(0, pre.length - 1),
                {
                  ...last,
                  content: `${last?.content}${e?.content.replace(/\\x0A/g, '\n')}`,
                },
              ];
            });
          } else if (e.type === 'remove') {
            setMessages((pre) => {
              const last = pre[pre.length - 1];
              return [
                ...pre.slice(0, pre.length - 1),
                {
                  ...last,
                  content: '',
                },
              ];
            });
          } else {
            try {
              const info = JSON.parse(e.content);
              setChatTotal(info.chatTotal);
              setLimitStatus(info.limitStatus);
              setSelectChatId(info.topicId);
              setMessages((pre) => {
                const last = pre[pre.length - 1];
                return [
                  ...pre.slice(0, pre.length - 1),
                  {
                    ...last,
                    id: info.id,
                  },
                ];
              });
            } catch (error) {}
          }
        },
        abortCtrl: abortCtrl,
      });
      setMessages((pre) => [...pre]);
      setLoading(false);
      setResponding(false);
      setTimeout(() => {
        throttleScroll();
      }, 200);
      setRefreshHistory((pre) => pre + 1);
    } catch (error) {
      setMessages((pre) => slice(pre, 0, pre.length - 2));
      AntdMessage.info(i18n('frequency high'));
      setValue(preQuestion.current || '');
      setLoading(false);
      setResponding(false);
    }
  };
  const i18n = useI18n();

  const cancel = () => {
    abortCtrl?.abort();
  };

  const onScroll = (e: HTMLElement) => {
    const bottomHeight = e.scrollTop + e.clientHeight;
    const isHitBottom = bottomHeight >= e.scrollHeight - 80;
    canScroll.current = isHitBottom;
  };
  return (
    <div className="relative">
      <div onClick={() => setFold((pre) => !pre)} className="cursor-pointer">
        <img src={foldSvg} className="absolute top-6 left-6 w-8 h-8" alt="" />
      </div>
      <div className="absolute right-3 top-3">
        <PersonCenter isLogin={isLogin} setIsLogin={setIsLogin} />
      </div>
      <div className="h-[100vh] w-full mx-auto flex flex-col content-between items-center">
        <div
          className="grow overflow-y-auto w-full mt-[4.5rem]"
          onClick={() => {
            canScroll.current = false;
          }}
          onScroll={(e) => onScroll(e.currentTarget)}
        >
          <div className="w-[52rem] mx-auto h-full">
            {!showWelcome && (
              <Message
                responding={responding}
                messages={messages}
                loading={loading}
              />
            )}
            {showWelcome && (
              <div className="w-full h-full flex flex-col justify-center items-center">
                <div className="text-[#0CBF9F] text-2xl">
                  {i18n('hello I am carbonAi')}
                </div>
                <div className="text-base mb-8 text-center">
                  {i18n('aim to provide')}
                </div>
                <div className="flex gap-4">
                  {questionList?.data.data.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => {
                        if (limitStatus) {
                          return;
                        }
                        sendMsg(item.userContent);
                      }}
                      className="px-6 py-3 bg-[#F2F4F8] rounded-2xl text-sm text-[#04252B] cursor-pointer"
                    >
                      {item.userContent}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="w-[52rem] mx-auto">
          <AutoSizeInput
            cancel={cancel}
            isLogin={isLogin}
            value={value}
            onFocus={() => {
              canScroll.current = true;
              throttleScroll();
            }}
            disabled={loading || responding}
            responding={responding}
            limitStatus={limitStatus}
            chatTotal={chatTotal}
            onChange={(e) => setValue(e.target.value)}
            submit={() => sendMsg()}
          />
        </div>
      </div>
    </div>
  );
};
